import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { taskStatusListSchema } from "@/lib/task-status-options";

// Filters live in the URL so they survive refresh and can be shared/bookmarked.
// Off-contract values degrade to undefined instead of failing the route.
const crmTaskSearchSchema = z.object({
  status: taskStatusListSchema,
  priority: z.string().min(1).optional().catch(undefined),
  assigned_to: z.number().optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/_layout/crm/tasks")({
  validateSearch: crmTaskSearchSchema,
  component: CrmAdminTasksPage,
});

import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, LayoutGrid, List } from "lucide-react";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import { getTaskStatusOptions } from "@/lib/task-status-options";
import type { TaskStatus } from "@/types/task";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import { Button } from "@/components/ui/button";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";

import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";

import { CrmAdminTaskTable } from "@/components/crm/tables/CrmAdminTaskTable";
import { TaskKanbanBoard } from "@/components/crud/table/TaskKanbanBoard";
import { CrmTaskFormSheet } from "@/components/crm/forms/CrmTaskFormSheet";
import { SubtaskFormSheet } from "@/components/crud/forms/SubtaskFormSheet";
import { TaskReviewSheet } from "@/components/crud/forms/TaskReviewSheet";
import { CompleteTaskDialog } from "@/components/crud/forms/CompleteTaskDialog";
import {
  useFetchCrmTasks,
  useCrmTaskMutations,
  type CrmTaskOrdering,
} from "@/hooks/api/crm/useCrmTasks";
import { crmKeys } from "@/hooks/api/crm/keys";
// CrmTask is a re-export of the unified Task type, so TaskReviewSheet accepts it directly.
import type { CrmTask, CrmTaskPayload } from "@/types/crm";
import type { Subtask, SubtaskPayload } from "@/types/subtask";

// Shared HR lifecycle hooks, called with isAdmin = true (hits /api/tasks/admin/...)
import useStartTask from "@/hooks/api/tasks/useStartTask";
import useCompleteTask from "@/hooks/api/tasks/useCompleteTask";
import useSubmitTask from "@/hooks/api/tasks/approval/useSubmitTask";
import useStartSubtask from "@/hooks/api/tasks/subtask/useStartSubtask";
import useCompleteSubtask from "@/hooks/api/tasks/subtask/useCompleteSubtask";
import useCreateSubtask from "@/hooks/api/tasks/subtask/useCreateSubtask";
import useUpdateSubtask from "@/hooks/api/tasks/subtask/useUpdateSubtask";
import useDeleteSubtask from "@/hooks/api/tasks/subtask/useDeleteSubtask";
import useApproveTask from "@/hooks/api/tasks/approval/useApproveTask";
import useRejectTask from "@/hooks/api/tasks/approval/useRejectTask";
import useBulkApprove from "@/hooks/api/tasks/approval/useBulkApprove";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function CrmAdminTasksPage() {
  const queryClient = useQueryClient();

  // Bulk-approve hits AdminTaskViewSet.bulk_approve, which requires
  // task.task.approve (see CrmAdminTaskTable for the rest of the task-level gating).
  const { data: capabilities } = useMyPermissions();
  const canApproveTasks = can(capabilities, "task.task.approve");

  // Task lifecycle/subtask actions below hit the shared HR task endpoints
  // (`/api/tasks/admin/...`), which only know about `taskKeys` — they can't
  // invalidate the CRM tasks list this page reads from. Nudge it manually
  // whenever one of those mutations changes a row this page is showing.
  const invalidateCrmTasks = useCallback(
    () => queryClient.invalidateQueries({ queryKey: crmKeys.crmTasks.all }),
    [queryClient],
  );

  const { createCrmTask, updateCrmTask, deleteCrmTask, deferCrmTask } =
    useCrmTaskMutations();

  const startTask = useStartTask();
  const completeTask = useCompleteTask();
  const submitTask = useSubmitTask();
  const startSubtask = useStartSubtask();
  const completeSubtask = useCompleteSubtask();
  const createSubtask = useCreateSubtask();
  const updateSubtask = useUpdateSubtask();
  const deleteSubtask = useDeleteSubtask();
  const approveTask = useApproveTask();
  const rejectTask = useRejectTask();
  const bulkApproveTask = useBulkApprove();
  const taskApprovalRequired = useOrgSettingsStore(
    (s) => s.settings?.task_approval_required ?? true
  );

  // Filters / ordering / pagination
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        user__first_name: SortDirection;
        title: SortDirection;
        status: SortDirection;
        priority: SortDirection;
        started_at: SortDirection;
        completed_at: SortDirection;
        due_date: SortDirection;
        created_at: SortDirection;
      },
      CrmTaskOrdering
    >({
      user__first_name: null,
      title: null,
      status: null,
      priority: null,
      started_at: null,
      completed_at: null,
      due_date: null,
      created_at: null,
    });

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const statusFilter = searchParams.status;
  const priorityFilter = searchParams.priority;
  const assignedTo = searchParams.assigned_to;
  const setStatusFilter = useCallback(
    (values: TaskStatus[]) =>
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          status: values.length > 0 ? values : undefined,
        }),
        replace: true,
      }),
    [navigate]
  );
  const setPriorityFilter = useCallback(
    (value: string | undefined) =>
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, priority: value || undefined }),
        replace: true,
      }),
    [navigate]
  );
  const setAssignedTo = useCallback(
    (value: number | undefined) =>
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, assigned_to: value }),
        replace: true,
      }),
    [navigate]
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.q ?? "");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);
  const [showFilters, setShowFilters] = useState(false);

  // Persist the (debounced) search text in the URL alongside the dropdown filters.
  useEffect(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, navigate]);

  // Sheets / dialogs
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);

  const [isSubtaskSheetOpen, setIsSubtaskSheetOpen] = useState(false);
  const [isSubtaskViewOnly, setIsSubtaskViewOnly] = useState(false);
  const [parentTask, setParentTask] = useState<CrmTask | null>(null);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);

  const [taskToDelete, setTaskToDelete] = useState<CrmTask | null>(null);
  const [taskToComplete, setTaskToComplete] = useState<CrmTask | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [subtaskToDelete, setSubtaskToDelete] = useState<{
    subtask: Subtask;
    taskId: number;
  } | null>(null);
  const [isSubtaskDeleteOpen, setIsSubtaskDeleteOpen] = useState(false);

  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [isReviewSheetOpen, setIsReviewSheetOpen] = useState(false);
  const [taskToReview, setTaskToReview] = useState<CrmTask | null>(null);

  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const KANBAN_PAGE_SIZE = 100;

  const crmTasksQuery = useFetchCrmTasks(
    viewMode === "kanban"
      ? {
          page: 1,
          page_size: KANBAN_PAGE_SIZE,
          search: debouncedSearch || undefined,
          status_in: statusFilter?.length ? statusFilter : undefined,
          priority: priorityFilter || undefined,
          assigned_to: assignedTo ? String(assignedTo) : undefined,
        }
      : {
          page,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          status_in: statusFilter?.length ? statusFilter : undefined,
          priority: priorityFilter || undefined,
          assigned_to: assignedTo ? String(assignedTo) : undefined,
          ordering: getApiOrdering(),
        }
  );
  const crmTasks = crmTasksQuery.data?.results ?? [];
  const count = crmTasksQuery.data?.count ?? 0;

  const isMutating =
    createCrmTask.isPending ||
    updateCrmTask.isPending ||
    deleteCrmTask.isPending ||
    startTask.isPending ||
    completeTask.isPending ||
    submitTask.isPending ||
    startSubtask.isPending ||
    completeSubtask.isPending ||
    createSubtask.isPending ||
    updateSubtask.isPending ||
    deleteSubtask.isPending ||
    approveTask.isPending ||
    rejectTask.isPending ||
    bulkApproveTask.isPending;
  const isLoading = crmTasksQuery.isPending || isMutating;
  const isFetching = crmTasksQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    priorityFilter,
    assignedTo,
    ordering,
    viewMode,
    setPage,
  ]);

  const resetFilters = useCallback(() => {
    setOrdering({
      user__first_name: null,
      title: null,
      status: null,
      priority: null,
      started_at: null,
      completed_at: null,
      due_date: null,
      created_at: null,
    });
    navigate({ to: ".", search: {}, replace: true });
    setSearchQuery("");
    resetDebounce();
    setPage(1);
  }, [navigate, resetDebounce, setOrdering, setPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Task CRUD
  const handleCreate = async (data: CrmTaskPayload) => {
    await createCrmTask.mutateAsync(data);
    toast.success("Task created!");
    setIsSheetOpen(false);
  };

  const handleEdit = async (id: number, data: Partial<CrmTaskPayload>) => {
    await updateCrmTask.mutateAsync({ id, payload: data });
    toast.success("Task updated!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    try {
      await deleteCrmTask.mutateAsync({ id: taskToDelete.id });
      toast.success("Task deleted!");
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Task lifecycle (admin)
  const handleStartTask = async (task: CrmTask) => {
    try {
      await startTask.mutateAsync({ id: task.id, isAdmin: true });
      toast.success("Task started successfully!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeferTask = async (task: CrmTask) => {
    try {
      await deferCrmTask.mutateAsync({ id: task.id });
      toast.success("Task deferred!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Completion opens a dialog so the user can record what was done.
  const handleCompleteTask = (task: CrmTask) => {
    setTaskToComplete(task);
  };

  const handleCompleteConfirm = async (remarks: string) => {
    if (!taskToComplete) return;
    await completeTask.mutateAsync({
      id: taskToComplete.id,
      isAdmin: true,
      payload: { completion_remarks: remarks },
    });
    toast.success("Task completed successfully!");
    invalidateCrmTasks();
    setTaskToComplete(null);
  };

  const handleSubmitTask = async (task: CrmTask) => {
    try {
      await submitTask.mutateAsync({ id: task.id, isAdmin: true });
      toast.success("Task submitted successfully!");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "";
      if (msg.toLowerCase().includes("not required")) {
        try {
          await startTask.mutateAsync({ id: task.id, isAdmin: true });
          toast.success("Task started successfully!");
        } catch {
          // error toast comes from the mutation's meta.errorMessage
        }
      }
    }
    invalidateCrmTasks();
  };

  // Review (approve / reject / bulk)
  const handleReviewTask = (taskId: number) => {
    const task = crmTasks.find((t) => t.id === taskId);
    if (task) {
      setTaskToReview(task);
      setIsReviewSheetOpen(true);
    }
  };

  const handleApproveConfirm = async (id: number, remarks: string) => {
    try {
      await approveTask.mutateAsync({ id, payload: { remarks } });
      toast.success("Task approved successfully!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleRejectConfirm = async (id: number, remarks: string) => {
    try {
      await rejectTask.mutateAsync({ id, payload: { remarks } });
      toast.success("Task rejected successfully!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTaskIds.length === 0) return;
    try {
      await bulkApproveTask.mutateAsync({ task_ids: selectedTaskIds });
      toast.success("Tasks approved successfully!");
      setSelectedTaskIds([]);
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Subtask lifecycle (admin)
  const handleStartSubtask = async (subtask: Subtask, taskId: number) => {
    try {
      await startSubtask.mutateAsync({ taskId, id: subtask.id, isAdmin: true });
      toast.success("Subtask started successfully!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCompleteSubtask = async (subtask: Subtask, taskId: number) => {
    try {
      await completeSubtask.mutateAsync({
        taskId,
        id: subtask.id,
        isAdmin: true,
      });
      toast.success("Subtask completed successfully!");
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Subtask CRUD (admin)
  const handleAddSubtask = (task: CrmTask) => {
    setParentTask(task);
    setEditingSubtask(null);
    setIsSubtaskViewOnly(false);
    setIsSubtaskSheetOpen(true);
  };

  const handleViewSubtask = (subtask: Subtask, taskId: number) => {
    setParentTask(crmTasks.find((t) => t.id === taskId) ?? null);
    setEditingSubtask(subtask);
    setIsSubtaskViewOnly(true);
    setIsSubtaskSheetOpen(true);
  };

  const handleEditSubtask = (subtask: Subtask, taskId: number) => {
    setParentTask(crmTasks.find((t) => t.id === taskId) ?? null);
    setEditingSubtask(subtask);
    setIsSubtaskViewOnly(false);
    setIsSubtaskSheetOpen(true);
  };

  const handleSubtaskFormSubmit = async (data: SubtaskPayload) => {
    if (!parentTask) return;
    try {
      if (editingSubtask) {
        await updateSubtask.mutateAsync({
          taskId: parentTask.id,
          id: editingSubtask.id,
          payload: { ...data, task: parentTask.id },
          isAdmin: true,
        });
        toast.success("Subtask updated successfully!");
      } else {
        await createSubtask.mutateAsync({
          taskId: parentTask.id,
          payload: { ...data, task: parentTask.id },
          isAdmin: true,
        });
        toast.success("Subtask created successfully!");
      }
      setIsSubtaskSheetOpen(false);
      setEditingSubtask(null);
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeleteSubtaskClick = (subtask: Subtask, taskId: number) => {
    setSubtaskToDelete({ subtask, taskId });
    setIsSubtaskDeleteOpen(true);
  };

  const handleDeleteSubtaskConfirm = async () => {
    if (!subtaskToDelete) return;
    try {
      await deleteSubtask.mutateAsync({
        taskId: subtaskToDelete.taskId,
        id: subtaskToDelete.subtask.id,
        isAdmin: true,
      });
      toast.success("Subtask deleted successfully!");
      setIsSubtaskDeleteOpen(false);
      setSubtaskToDelete(null);
      invalidateCrmTasks();
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="CRM Tasks"
          description="Manage, assign, and track CRM tasks efficiently."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditingTask(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Create Task"
          isLoading={isFetching}
        >
          {taskApprovalRequired && canApproveTasks && selectedTaskIds.length > 0 && (
            <Button
              onClick={handleBulkApprove}
              disabled={isFetching}
              variant="secondary"
            >
              <Check className="h-4 w-4" />
              Bulk Approve ({selectedTaskIds.length})
            </Button>
          )}
          <div className="flex items-center rounded-md border overflow-hidden">
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("kanban")}
              title="Kanban view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search tasks..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} className="w-[220px]" />
          <MultiSelectFilter
            placeholder="Status"
            options={getTaskStatusOptions({
              taskApprovalRequired,
              includeDeferred: true,
            })}
            values={statusFilter ?? []}
            onChange={setStatusFilter}
          />
          <SelectFilter
            placeholder="Priority"
            options={[
              { label: "Low", value: "Low" },
              { label: "Normal", value: "Normal" },
              { label: "High", value: "High" },
              { label: "All", value: "clear" },
            ]}
            value={priorityFilter}
            onChange={(value) =>
              setPriorityFilter(value === "clear" ? undefined : value)
            }
          />
        </FiltersPanel>

        {viewMode === "table" ? (
          <>
            <CrmAdminTaskTable
              tasks={crmTasks}
              onTitleClick={(id) =>
                navigate({ to: "/admin/crm/tasks/$id", params: { id: String(id) } })
              }
              onView={(task) => {
                setEditingTask(task);
                setIsViewOnly(true);
                setIsSheetOpen(true);
              }}
              onEdit={(task) => {
                setEditingTask(task);
                setIsViewOnly(false);
                setIsSheetOpen(true);
              }}
              onDelete={(task) => {
                setTaskToDelete(task);
                setIsDeleteDialogOpen(true);
              }}
              onAddSubtask={handleAddSubtask}
              onViewSubtask={handleViewSubtask}
              onEditSubtask={handleEditSubtask}
              onDeleteSubtask={handleDeleteSubtaskClick}
              onStartTask={handleStartTask}
              onCompleteTask={handleCompleteTask}
              onDeferTask={handleDeferTask}
              onStartSubtask={handleStartSubtask}
              onCompleteSubtask={handleCompleteSubtask}
              isLoading={isLoading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
              onReviewTask={handleReviewTask}
              onSubmitTask={handleSubmitTask}
              selectedTaskIds={selectedTaskIds}
              onSelectionChange={setSelectedTaskIds}
            />

            <PaginationComponent
              count={count}
              page={page}
              onPageChange={handlePageChange}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        ) : (
          <TaskKanbanBoard
            tasks={crmTasks}
            taskApprovalRequired={taskApprovalRequired}
            includeDeferred
            isLoading={isLoading}
            onView={(task) => {
              setEditingTask(task);
              setIsViewOnly(true);
              setIsSheetOpen(true);
            }}
            onEdit={(task) => {
              setEditingTask(task);
              setIsViewOnly(false);
              setIsSheetOpen(true);
            }}
            onDelete={(task) => {
              setTaskToDelete(task);
              setIsDeleteDialogOpen(true);
            }}
            onStartTask={handleStartTask}
            onSubmitTask={handleSubmitTask}
            onCompleteTask={handleCompleteTask}
            onReviewTask={handleReviewTask}
            onDeferTask={handleDeferTask}
          />
        )}

        <CrmTaskFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onCreate={handleCreate}
          onEdit={handleEdit}
          editingTask={editingTask}
          isLoading={isMutating}
          onEditClick={isViewOnly ? () => setIsViewOnly(false) : undefined}
        />

        <SubtaskFormSheet
          isAdmin={true}
          isOpen={isSubtaskSheetOpen}
          isViewOnly={isSubtaskViewOnly}
          onClose={() => {
            setIsSubtaskSheetOpen(false);
            setEditingSubtask(null);
          }}
          onSubmit={handleSubtaskFormSubmit}
          parentTaskTitle={parentTask?.title}
          isLoading={createSubtask.isPending || updateSubtask.isPending}
          editingSubtask={editingSubtask}
        />

        <CompleteTaskDialog
          isOpen={!!taskToComplete}
          onClose={() => setTaskToComplete(null)}
          taskTitle={taskToComplete?.title}
          isLoading={completeTask.isPending}
          onConfirm={handleCompleteConfirm}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Task"
          description={`Delete task "${taskToDelete?.title}"? This action cannot be undone.`}
          itemName={taskToDelete?.title}
          isLoading={deleteCrmTask.isPending}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={isSubtaskDeleteOpen}
          onClose={() => {
            setIsSubtaskDeleteOpen(false);
            setSubtaskToDelete(null);
          }}
          onConfirm={handleDeleteSubtaskConfirm}
          title="Delete Subtask"
          description={`Delete subtask "${subtaskToDelete?.subtask.title}"? This action cannot be undone.`}
          itemName={subtaskToDelete?.subtask.title}
          isLoading={deleteSubtask.isPending}
          destructive
        />

        <TaskReviewSheet
          isOpen={isReviewSheetOpen}
          onClose={() => setIsReviewSheetOpen(false)}
          isLoading={approveTask.isPending || rejectTask.isPending}
          task={taskToReview}
          onApprove={handleApproveConfirm}
          onReject={handleRejectConfirm}
        />
      </div>
    </SidebarInset>
  );
}
